import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';

import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { setActiveFilter } from '../../../../store/notification-hub/notification-hub.actions';
import type { Notification } from '../../../../store/notification-hub/notification-hub.types';
import { NotificationFilter } from '../../../../store/notification-hub/notification-hub.types';
import Global from '../../../../store/global-selectors';

import { Segment } from '../../../../services/analytics-services/segment';
import { ALERTS_PANEL_FILTERED_VIEW } from '../../../../constants/analytics-events';
import { messages } from './messages';
import style from './style.module.less';

type FilterButtonProps = {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
  filter: NotificationFilter;
};

const FilterButton = ({ label, count, isActive, onClick, filter }: FilterButtonProps) => {
  const shouldShowBadge = filter !== NotificationFilter.ALL && count > 0;

  return (
    <Button className={classnames(style['filter-button'], { [style.active]: isActive })} onClick={onClick} theme={THEME_CONSTANTS.unstyled}>
      <FlexRow className={style['filter-button-inner']} gap={Gap.Space4}>
        <Text element='span' textType={TextType.Paragraph} value={label} />
        {shouldShowBadge && <span className={style['count-badge']}>{count}</span>}
      </FlexRow>
    </Button>
  );
};

export const NotificationFilters = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const activeFilter = useSelector(Global.selectNotificationHubActiveFilter) ?? NotificationFilter.ALL;
  const allNotifications = useSelector(Global.selectNotificationHubAllNotifications) ?? [];
  const unreadCount = useSelector(Global.selectNotificationHubUnreadCount) ?? 0;
  const allCount = allNotifications.filter((n: Notification) => !n.isDismissed).length;

  const handleFilterClick = useCallback(
    (filter: NotificationFilter) => () => {
      Segment.trackEvent(ALERTS_PANEL_FILTERED_VIEW, {
        filter: filter === NotificationFilter.ALL ? 'all' : 'unread',
      });
      dispatch(setActiveFilter(filter));
    },
    [dispatch],
  );

  return (
    <FlexRow className={style['filter-container']}>
      <FlexRow className={style['tab-group']}>
        <FilterButton
          count={unreadCount}
          filter={NotificationFilter.UNREAD}
          isActive={activeFilter === NotificationFilter.UNREAD}
          label={intl.formatMessage(messages.filterUnread)}
          onClick={handleFilterClick(NotificationFilter.UNREAD)}
        />
        <FilterButton
          count={allCount}
          filter={NotificationFilter.ALL}
          isActive={activeFilter === NotificationFilter.ALL}
          label={intl.formatMessage(messages.filterAll)}
          onClick={handleFilterClick(NotificationFilter.ALL)}
        />
      </FlexRow>
    </FlexRow>
  );
};
